import React from 'react';
import { PauseCircle } from 'lucide-react';

/**
 * Shared participant status badges (FECOA-1885).
 *
 * Before this, "Inactive" and "Suspended" were hand-rolled per surface and had
 * drifted: Inactive was rose-with-border on the participant profile, flat red
 * with no dark mode on the site roster, and plain text (no badge) elsewhere;
 * Suspended was gray in one place and amber in another. These components are
 * the single source of truth so every list, profile, assessment detail, and
 * report renders the two states identically.
 *
 *   - Active     → emerald  (participant is enrolled and active)
 *   - Inactive   → rose     (participant has been deactivated)
 *   - Suspended  → amber    (an assessment frozen by participant deactivation)
 */

const base =
  'inline-flex items-center gap-1 whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium border';

const tone = {
  active:
    'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40 dark:border-emerald-500/30',
  inactive:
    'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/40 dark:border-rose-500/30',
  suspended:
    'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40 dark:border-amber-500/30',
};

/**
 * Active / Inactive badge for a participant.
 * @param {boolean} isActive
 */
export const ParticipantActiveBadge = ({ isActive, className = '' }) => (
  <span className={`${base} ${isActive ? tone.active : tone.inactive} ${className}`}>
    {isActive ? 'Active' : 'Inactive'}
  </span>
);

/**
 * Suspended badge for an assessment/survey frozen by participant deactivation.
 */
export const SuspendedBadge = ({ className = '' }) => (
  <span className={`${base} ${tone.suspended} ${className}`}>
    <PauseCircle className="w-3 h-3" aria-hidden="true" />
    Suspended
  </span>
);

export default ParticipantActiveBadge;
